import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Calendar } from "lucide-react";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import { JOURNAL as STATIC_JOURNAL } from "@/data/journal";
import { api } from "@/lib/api";

export default function Journal() {
  const [articles, setArticles] = useState(STATIC_JOURNAL);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/journal");
        if (Array.isArray(data) && data.length > 0) {
          // Merge: DB articles first, then static ones whose slug isn't in DB
          const dbSlugs = new Set(data.map((d) => d.slug));
          const staticOnly = STATIC_JOURNAL.filter((s) => !dbSlugs.has(s.slug));
          setArticles([...data, ...staticOnly]);
        }
      } catch { /* keep static fallback */ }
    })();
  }, []);

  return (
    <div data-testid="journal-index" className="pt-28 md:pt-36 pb-28">
      <SEO
        title="The Journal — Case studies in vascular & endovascular IR · Dr Tegbir Vascular"
        description="Anonymised case studies from Dr Tegbir Sidhu's interventional radiology practice — fibroids, PAD, PAE, aortic aneurysm and more. Every story ends with what we learned."
        keywords={["vascular case study india", "IR patient story mohali", "endovascular case study", "interventional radiology outcomes"]}
      />
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <FadeUp>
          <Breadcrumbs trail={[{ to: "/", label: "Home" }, { label: "Journal" }]} />
          <div className="mt-8 kbd">— The Journal</div>
          <h1 className="mt-6 font-serif text-6xl md:text-[8vw] leading-[0.96] tracking-tightest">
            <MaskedReveal text={"Cases,"}/>
            <MaskedReveal text={"anonymised."} delay={0.1} className="italic font-normal"/>
            <MaskedReveal text={"Lessons, unedited."} delay={0.2}/>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-ink-muted leading-relaxed">
            Not testimonials. Not marketing. Anonymised case studies from Dr Sidhu's practice — the imaging read, the conversation, the plan, the follow-up. And, at the end of each, what the case taught us. Individual outcomes vary; nothing here replaces personal medical advice.
          </p>
        </FadeUp>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-2 gap-8">
          {articles.map((a, i) => (
            <FadeUp key={a.slug} delay={i * 0.05}>
              <Link to={`/journal/${a.slug}`} data-testid={`journal-card-${a.slug}`} className="group block hairline bg-white/60 overflow-hidden hover:bg-white transition-colors">
                <div className="aspect-[16/9] overflow-hidden relative">
                  <img src={a.hero || "https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&w=1600&q=80"} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" loading="lazy"/>
                  <div className="noise-overlay absolute inset-0"/>
                </div>
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-3 kbd text-ink-muted">
                    <span className="inline-flex items-center gap-1"><Calendar size={11}/> {a.date}</span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1"><Clock size={11}/> {a.readTime || "4 min"}</span>
                  </div>
                  <h2 className="mt-4 font-serif text-3xl md:text-4xl tracking-tightest leading-[1.05] group-hover:text-crimson transition-colors">{a.title}</h2>
                  <p className="mt-3 text-ink-muted leading-relaxed">{a.deck}</p>
                  {a.tags && a.tags.length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {a.tags.map((t) => <span key={t} className="kbd border border-ink/15 px-2 py-1 rounded-full">{t}</span>)}
                    </div>
                  )}
                  <div className="mt-6 inline-flex items-center gap-2 text-sm text-ink group-hover:text-crimson">
                    Read the case <ArrowUpRight size={14}/>
                  </div>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </div>
  );
}
